import React, { useState, useRef } from 'react';
import { motion, useInView, useScroll, useSpring } from 'framer-motion';
import '../../styles/cam360-help-center.css';
import {
  Search,
  BookOpen,
  Users,
  Lightbulb,
  Navigation,
  Layers,
  Brain,
  Database,
  Shield,
  Settings,
  HelpCircle,
  AlertTriangle,
  FileText,
  Mail,
  X,
  Home,
  Target,
  TrendingUp,
  TrendingDown,
  BarChart3,
  CheckCircle,
  Clock,
  DollarSign,
  FileText as FileContract,
  Wrench,
  Building2,
  MessageSquare,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Zap,
  PieChart,
  Calendar,
  AlertCircle,
  Info,
  Star,
  Globe,
  Briefcase,
  FolderOpen,
  CreditCard,
  Activity,
  Sparkles
} from 'lucide-react';

const CAM360HelpCenter = () => {
  const [activeSection, setActiveSection] = useState('introduction');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [expandedModule, setExpandedModule] = useState(null);
  const [showPharmaExample, setShowPharmaExample] = useState(false);
  const [showRealEstateExample, setShowRealEstateExample] = useState(false);
  const [showManufacturingExample, setShowManufacturingExample] = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Navigation sections
  const navSections = [
    { id: 'introduction', title: 'Introduction', icon: Home },
    { id: 'functional-flow', title: 'Functional Flow', icon: Navigation },
    { id: 'modules', title: 'Module Details', icon: Layers },
    { id: 'journey-map', title: 'Journey Map', icon: Target },
    { id: 'glossary', title: 'Glossary', icon: BookOpen },
    { id: 'faq', title: 'FAQ', icon: HelpCircle },
    { id: 'contact', title: 'Contact Support', icon: Mail }
  ];

  // Scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(sectionId);
    }
  };

  // Module data
  const modules = [
    {
      id: 'projects',
      title: 'Projects',
      icon: FolderOpen,
      description: 'The starting point for everything. Every activity in CAM360 belongs to a project.',
      pills: ['Scope', 'Timeline', 'Budget', 'Team'],
      content: {
        overview: 'The starting point for everything. Every activity in CAM360 belongs to a project.',
        whenToUse: 'whenever you initiate a new engagement or program.',
        steps: [
          'Go to Projects > New Project',
          'Enter project basics (name, category, business unit, status)',
          'Set time frame (start date, end date, or estimated duration)',
          'Define initial budget and cost center',
          'Use other tabs to add team, communication rules, and approvals'
        ],
        tip: 'Think of a project as a container. Once it exists, you can attach milestones, subcontractors, contracts, assets, and financial data to it. This is what makes reports meaningful.',
        examples: ['Prime Real Estate – Residential Tower A', 'Data Center Migration', 'Factory Expansion Phase 2']
      }
    },
    {
      id: 'milestones',
      title: 'Milestones',
      icon: Target,
      description: 'Break the project into measurable chunks with dates and responsibilities.',
      pills: ['Progress bars', 'Status colors', 'Due dates'],
      content: {
        overview: 'Track project progress with visual milestones and status indicators.',
        whatYouCapture: [
          'Milestone name and description',
          'Target due date and priority',
          'Status (Pending, In-Progress, Completed, Overdue)',
          'Owner or responsible person'
        ],
        note: 'The Milestone Tracking screen lets you filter, sort, and quickly see which parts of the project are on schedule or at risk.',
        statusColors: {
          completed: '#3BAA72',
          inProgress: '#4678be',
          delayed: '#E57373',
          pending: '#687484'
        },
        progressBars: [
          { label: 'Design', percent: 100 },
          { label: 'Construction', percent: 65 },
          { label: 'Testing', percent: 20 },
          { label: 'Handover', percent: 5 }
        ]
      }
    },
    {
      id: 'subcontracting',
      title: 'Subcontracting',
      icon: Building2,
      description: 'Track external vendors and service providers who deliver part of the project.',
      pills: ['Vendors', 'Scopes of work', 'Performance'],
      content: {
        overview: 'Manage external vendors and subcontractors seamlessly.',
        steps: [
          'Create subcontractor records for each project',
          'Define what portion of the work they are responsible for',
          'Associate them with milestones or phases',
          'Prepare the base for contracts and financial tracking'
        ],
        example: 'Electrical contractor, HVAC vendor, civil works partner. Each one is tied to milestones and later linked to a contract.'
      }
    },
    {
      id: 'contracts',
      title: 'Contracts',
      icon: FileContract,
      description: 'Store and track the commercial and legal agreements for the project.',
      pills: ['Scope of work', 'Payment terms', 'Approvals'],
      content: {
        overview: 'Contracts define scope of work, payment terms, and project obligations.',
        steps: [
          'Use contract templates or create custom ones',
          'Capture SLAs, deliverables, and obligations',
          'Define billing milestones and retention percentages',
          'Record status (Draft, Under Review, Signed, Closed)'
        ],
        note: 'Contracts connect Subcontractors to Finance. They answer "what was promised and under which conditions?".',
        lifecycle: ['Draft', 'Review', 'Approval', 'Signed', 'Active', 'Completed']
      }
    },
    {
      id: 'assets',
      title: 'Assets',
      icon: Wrench,
      description: 'Link plant, machinery, tools, and digital assets to projects and milestones.',
      pills: ['Allocation', 'Utilization', 'Cost'],
      content: {
        overview: 'Assets are physical or digital resources assigned to projects.',
        steps: [
          'Register key assets used on the project',
          'Assign them to phases or locations',
          'Track usage, downtime, and maintenance',
          'Capture rental or internal charge-out costs'
        ],
        outcome: 'You can see which assets are underused, overused, or generating unexpected costs, and adjust plans or budgets accordingly.'
      }
    },
    {
      id: 'finance',
      title: 'Finance',
      icon: DollarSign,
      description: 'Combine project, contract, and asset data to understand financial health.',
      pills: ['Budget vs Actual', 'Cost breakdown', 'Margin'],
      content: {
        overview: 'Track budget vs actual costs, revenue, and spending across all categories.',
        steps: [
          'Set initial project budget and track revisions',
          'Monitor labour, subcontracting, asset, and material costs',
          'Compare planned vs actuals over time',
          'View profitability at project or portfolio level'
        ],
        chartData: [
          { label: 'Budget', percent: 100 },
          { label: 'Committed', percent: 78 },
          { label: 'Actual', percent: 64 }
        ]
      }
    },
    {
      id: 'ai-intelligence',
      title: 'AI Intelligence',
      icon: Brain,
      description: 'Use project data to highlight risks, anomalies, and opportunities.',
      pills: ['Risk alerts', 'Pattern detection', 'Decision support'],
      content: {
        overview: 'AI Intelligence provides proactive alerts and recommendations to keep projects on track.',
        capabilities: [
          'Highlight projects likely to overrun budget or schedule',
          'Spot contracts with unusual terms or frequent changes',
          'Flag assets with high downtime vs usage',
          'Suggest actions based on similar past projects'
        ],
        note: 'AI works best when projects, milestones, contracts, and finance data are kept up-to-date. The more you use CAM360, the smarter the recommendations become.'
      }
    },
    {
      id: 'collaboration',
      title: 'Collaboration',
      icon: MessageSquare,
      description: 'Keep conversations, decisions, and documents connected directly to the work.',
      pills: ['Comments', 'Mentions', 'Notifications'],
      content: {
        overview: 'Keep your team connected with comments, mentions, and file sharing.',
        features: [
          'Discuss milestones, contracts, or issues in context',
          'Mention colleagues to draw their attention',
          'Upload supporting documents and link them to records',
          'Receive in-app or email notifications for key events'
        ],
        note: 'Instead of scattered emails and spreadsheets, collaboration inside CAM360 keeps history attached to each project element.'
      }
    }
  ];

  // FAQ data - Updated with better questions from HTML file
  const faqItems = [
    {
      id: 1,
      question: 'Where should I start in CAM360?',
      answer: 'Always begin with Projects. Create a project, define its basic details, and then move to milestones, subcontracting, contracts, assets, and finance as needed.'
    },
    {
      id: 2,
      question: 'How do I know if my project is on track?',
      answer: 'Use the Milestone Tracking screen. It shows status, progress percentages, and overdue markers. Combined with finance reports, it provides a clear health check.'
    },
    {
      id: 3,
      question: 'How are contracts linked to projects?',
      answer: 'Each contract is created in the context of a specific project (and usually a subcontractor). This ensures that commitments, invoices, and risks are always tied back to the right project.'
    },
    {
      id: 4,
      question: 'Can I see budget vs actuals in one place?',
      answer: 'Yes. The Finance module provides budget vs actual, committed spend, and profitability views at multiple levels (project or portfolio).'
    },
    {
      id: 5,
      question: 'What does the AI module actually do?',
      answer: 'The AI module analyzes existing data (dates, costs, contracts, history) to highlight potential risks, anomalies, and improvement ideas. It does not replace human decisions; it supports them.'
    },
    {
      id: 6,
      question: 'How do I update milestones?',
      answer: 'Navigate to the Milestones section, select the milestone you want to update, and change its status. You can mark it as completed, in progress, or delayed. The system will automatically update project progress.'
    },
    {
      id: 7,
      question: 'Which reports are available?',
      answer: 'CAM360 provides comprehensive reports including project dashboards, financial reports, asset utilization reports, contract analytics, and AI insights. Access them from the Reports section.'
    },
    {
      id: 8,
      question: 'Can I assign assets to multiple projects?',
      answer: 'Yes, assets can be allocated to multiple projects. The system tracks utilization and costs for each project separately, giving you complete visibility into asset usage.'
    }
  ];

  // Glossary terms - Updated with better definitions from HTML file
  const glossaryTerms = [
    { term: 'Project', definition: 'A temporary initiative with defined goals, scope, and budget.' },
    { term: 'Milestone', definition: 'A significant checkpoint or phase that indicates progress within a project.' },
    { term: 'Subcontractor', definition: 'An external vendor or partner responsible for delivering a part of the work.' },
    { term: 'Contract', definition: 'A formal agreement describing scope, terms, and commercial arrangements.' },
    { term: 'Asset', definition: 'Any resource (equipment, tools, digital licenses) used to deliver project work.' },
    { term: 'Budget', definition: 'Planned spend allocated to a project or cost category.' },
    { term: 'Actual Cost', definition: 'Real expenditure that has already occurred and been recorded.' },
    { term: 'Variance', definition: 'Difference between planned and actual values (e.g., cost or time).' },
    { term: 'ROI', definition: 'Return on Investment – the benefit gained vs the cost of the project.' },
    { term: 'AI Insight', definition: 'A system-generated observation or recommendation based on data patterns.' },
    { term: 'ROI', definition: 'Return on Investment - a measure of project profitability calculated as (Revenue - Cost) / Cost × 100.' },
    { term: 'Variance', definition: 'The difference between budgeted and actual costs, indicating whether a project is over or under budget.' },
    { term: 'AI Recommendation', definition: 'Intelligent suggestions provided by CAM360\'s AI system based on data analysis and predictive modeling.' }
  ];

  // Journey map steps
  const journeySteps = [
    { step: 1, title: 'Create Project', icon: FolderOpen, description: 'Set up your project with basic information, timeline, and budget.' },
    { step: 2, title: 'Add Milestones', icon: Target, description: 'Define key checkpoints and deliverables for your project.' },
    { step: 3, title: 'Link Subcontractors', icon: Building2, description: 'Add external vendors and assign them to specific milestones.' },
    { step: 4, title: 'Draft Contracts', icon: FileContract, description: 'Create contracts with scope of work and payment terms.' },
    { step: 5, title: 'Allocate Assets', icon: Wrench, description: 'Assign physical or digital assets to your project.' },
    { step: 6, title: 'Approve Financial Plan', icon: DollarSign, description: 'Review and approve the project budget and financial plan.' },
    { step: 7, title: 'Monitor Progress', icon: Activity, description: 'Track milestone completion and project status in real-time.' },
    { step: 8, title: 'Receive AI Alerts', icon: Brain, description: 'Get intelligent notifications about risks and opportunities.' },
    { step: 9, title: 'Collaborate', icon: MessageSquare, description: 'Communicate with team members, share files, and stay connected.' },
    { step: 10, title: 'Final Project Closure', icon: CheckCircle, description: 'Complete all deliverables, finalize contracts, and close the project.' }
  ];

  return (
    <div className="cam360-help-center">
      {/* Progress Bar */}
      <motion.div
        className="scroll-progress"
        style={{ scaleX }}
      />

      {/* Header */}
      <header className="help-header">
        <div className="header-content">
          <div className="header-left">
            <BookOpen className="header-icon" />
            <div>
              <h1>CAM360 Help Center</h1>
              <p>Your complete guide to managing projects, contracts, and assets</p>
            </div>
          </div>
          <div className="header-search">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Search help topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </header>

      <div className="help-layout">
        {/* Navigation Sidebar */}
        <nav className="help-sidebar">
          <ul className="nav-list">
            {navSections.map((section) => {
              const Icon = section.icon;
              return (
                <li key={section.id}>
                  <button
                    className={`nav-item ${activeSection === section.id ? 'active' : ''}`}
                    onClick={() => scrollToSection(section.id)}
                  >
                    <Icon size={18} />
                    <span>{section.title}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          
          {/* Industry Example Menu */}
          <div className="example-menu-section">
            <button
              className="example-menu-toggle"
              onClick={() => setShowPharmaExample(!showPharmaExample)}
            >
              <Briefcase size={18} />
              <span>Industry Example: Pharma</span>
              {showPharmaExample ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {showPharmaExample && (
              <motion.div
                className="example-menu-content"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="example-menu-item" onClick={() => scrollToSection('pharma-example')}>
                  <span>View Full Example</span>
                </div>
              </motion.div>
            )}
            
            <button
              className="example-menu-toggle"
              onClick={() => setShowRealEstateExample(!showRealEstateExample)}
              style={{ marginTop: '0.5rem' }}
            >
              <Building2 size={18} />
              <span>Industry Example: Real Estate</span>
              {showRealEstateExample ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {showRealEstateExample && (
              <motion.div
                className="example-menu-content"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="example-menu-item" onClick={() => scrollToSection('real-estate-example')}>
                  <span>View Full Example</span>
                </div>
              </motion.div>
            )}
            
            <button
              className="example-menu-toggle"
              onClick={() => setShowManufacturingExample(!showManufacturingExample)}
              style={{ marginTop: '0.5rem' }}
            >
              <Activity size={18} />
              <span>Industry Example: Manufacturing</span>
              {showManufacturingExample ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {showManufacturingExample && (
              <motion.div
                className="example-menu-content"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="example-menu-item" onClick={() => scrollToSection('manufacturing-example')}>
                  <span>View Full Example</span>
                </div>
              </motion.div>
            )}
          </div>
        </nav>

        {/* Main Content */}
        <main className="help-content">
          {/* Introduction Section */}
          <SectionContainer id="introduction" title="Introduction to CAM360">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="intro-hero">
                <h2>What is CAM360?</h2>
                <p className="lead">
                  CAM360 is a Contract & Asset Management platform built around <strong>projects</strong>. 
                  This help page explains what the application does, how the modules fit together, 
                  and the typical end-to-end flow you can follow in daily work.
                </p>
                <p style={{ marginTop: '1rem', fontSize: '16px', lineHeight: '1.7' }}>
                  <strong>CAM360</strong> is a web-based system that helps you manage complex projects 
                  where contracts, assets, and external partners play a major role. It provides a single place to:
                </p>
                <ul style={{ marginTop: '1rem', marginLeft: '1.5rem', lineHeight: '1.8' }}>
                  <li>Create and manage projects from initiation to closure.</li>
                  <li>Break work into milestones and track progress.</li>
                  <li>Manage subcontractors, vendors, and their commitments.</li>
                  <li>Generate and monitor contracts and financials.</li>
                  <li>Link physical and digital assets to the work where they are used.</li>
                  <li>Use AI-driven insights to predict risks and make better decisions.</li>
                </ul>
              </div>

              <div className="problem-solution">
                <h3>What Problems Does CAM360 Solve?</h3>
                <div className="problem-grid">
                  <motion.div
                    className="problem-card"
                    whileHover={{ scale: 1.02, y: -4 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <AlertCircle className="card-icon" />
                    <h4>Scattered Information</h4>
                    <p>No more switching between multiple systems. Everything is in one place.</p>
                  </motion.div>
                  <motion.div
                    className="problem-card"
                    whileHover={{ scale: 1.02, y: -4 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Clock className="card-icon" />
                    <h4>Project Delays</h4>
                    <p>Track milestones and get early warnings when projects are at risk.</p>
                  </motion.div>
                  <motion.div
                    className="problem-card"
                    whileHover={{ scale: 1.02, y: -4 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <DollarSign className="card-icon" />
                    <h4>Budget Overruns</h4>
                    <p>Monitor budget vs actual costs in real-time and prevent surprises.</p>
                  </motion.div>
                  <motion.div
                    className="problem-card"
                    whileHover={{ scale: 1.02, y: -4 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <FileContract className="card-icon" />
                    <h4>Contract Complexity</h4>
                    <p>Manage contract lifecycles from draft to completion with full visibility.</p>
                  </motion.div>
                </div>
              </div>

              <div className="who-uses">
                <h3>Who Should Use CAM360?</h3>
                <p style={{ marginBottom: '1rem', color: 'var(--text-body)' }}>
                  CAM360 is typically used by:
                </p>
                <div className="user-roles">
                  <motion.div
                    className="role-card"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                  >
                    <Briefcase className="role-icon" />
                    <h4>Project and Program Managers</h4>
                    <p>Track projects, milestones, and team performance</p>
                  </motion.div>
                  <motion.div
                    className="role-card"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    <FileContract className="role-icon" />
                    <h4>Contract Managers and Commercial Teams</h4>
                    <p>Manage contracts, vendors, and compliance</p>
                  </motion.div>
                  <motion.div
                    className="role-card"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <Wrench className="role-icon" />
                    <h4>Operations and Asset Management Teams</h4>
                    <p>Track assets, utilization, and maintenance</p>
                  </motion.div>
                  <motion.div
                    className="role-card"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    <DollarSign className="role-icon" />
                    <h4>Finance and Controlling Teams</h4>
                    <p>Monitor budgets, costs, and financial performance</p>
                  </motion.div>
                  <motion.div
                    className="role-card"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    <BarChart3 className="role-icon" />
                    <h4>Leadership Teams</h4>
                    <p>Need a consolidated view across all projects</p>
                  </motion.div>
                </div>
                <div className="phase-note" style={{ marginTop: '2rem' }}>
                  <Info size={18} />
                  <p>
                    <strong>Key idea:</strong> Everything in CAM360 hangs from a <strong>Project</strong>. 
                    Once a project is created, other modules like milestones, contracts, assets, and finance follow.
                  </p>
                </div>
              </div>

              <div className="modules-overview">
                <h3>Core Modules Overview</h3>
                <div className="modules-grid-intro">
                  {modules.map((module, index) => {
                    const Icon = module.icon;
                    return (
                      <motion.div
                        key={module.id}
                        className="module-chip"
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        whileHover={{ scale: 1.05 }}
                      >
                        <Icon size={20} />
                        <span>{module.title}</span>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </SectionContainer>

          {/* Functional Flow Section */}
          <SectionContainer id="functional-flow" title="High-Level Functional Flow">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="section-intro">
                A project-centric journey from creation to closure. This is the recommended way to use CAM360. 
                You do not have to use every module on day one, but following this order ensures you get 
                consistent data and meaningful reports.
              </p>

              <div className="flow-diagram">
                {[
                  { step: 'Create Project', icon: FolderOpen, color: '#4678be' },
                  { step: 'Define Milestones', icon: Target, color: '#3BAA72' },
                  { step: 'Add Subcontractors', icon: Building2, color: '#F2B740' },
                  { step: 'Create Contracts', icon: FileContract, color: '#8B5CF6' },
                  { step: 'Assign Assets', icon: Wrench, color: '#E57373' },
                  { step: 'Track Finance', icon: DollarSign, color: '#17a2b8' },
                  { step: 'AI Insights', icon: Brain, color: '#8B5CF6' },
                  { step: 'Collaboration', icon: MessageSquare, color: '#3BAA72' }
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <React.Fragment key={index}>
                      <motion.div
                        className="flow-step"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        whileHover={{ scale: 1.1, y: -8 }}
                      >
                        <div className="flow-icon-wrapper" style={{ backgroundColor: `${item.color}20`, borderColor: item.color }}>
                          <Icon size={24} style={{ color: item.color }} />
                        </div>
                        <span>{item.step}</span>
                      </motion.div>
                      {index < 7 && (
                        <motion.div
                          className="flow-arrow"
                          initial={{ scaleX: 0 }}
                          whileInView={{ scaleX: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                        >
                          <ArrowRight size={20} />
                        </motion.div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              <p className="muted-module" style={{ marginTop: '0.75rem' }}>
                You do not have to use every module on day one, but following this
                order ensures you get consistent data and meaningful reports.
              </p>
            </motion.div>
          </SectionContainer>

          {/* Module Details Section */}
          <SectionContainer id="modules" title="Detailed Module Explanation">
            <div className="modules-detailed">
              {modules.map((module, index) => {
                const Icon = module.icon;
                const isExpanded = expandedModule === module.id;
                return (
                  <motion.div
                    key={module.id}
                    className="module-detail-card"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ boxShadow: '0 12px 24px rgba(70, 120, 190, 0.15)' }}
                  >
                    <div>
                      <div
                        className="module-header"
                        onClick={() => setExpandedModule(isExpanded ? null : module.id)}
                      >
                        <div className="module-title-section">
                          <Icon className="module-icon" size={24} />
                          <div>
                            <h3>{module.title}</h3>
                            <p>{module.description}</p>
                          </div>
                        </div>
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </div>
                      {module.pills && (
                        <div className="pill-row-module">
                          {module.pills.map((pill, i) => (
                            <span key={i} className="pill-module">{pill}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <motion.div
                      className="module-content"
                      initial={false}
                      animate={{
                        height: isExpanded ? 'auto' : 0,
                        opacity: isExpanded ? 1 : 0
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      {isExpanded && (
                        <div className="module-details">
                          {/* Two-column layout matching HTML structure */}
                          <div className="two-col-module">
                            <div>
                              {/* Left column content */}
                              {module.content.whenToUse && (
                                <p><strong>When to use:</strong> {module.content.whenToUse}</p>
                              )}
                              
                              {module.content.steps && (
                                <ul className="list-module">
                                  {module.content.steps.map((step, i) => (
                                    <li key={i}>{step}</li>
                                  ))}
                                </ul>
                              )}
                              
                              {module.content.whatYouCapture && (
                                <>
                                  <p><strong>What you capture:</strong></p>
                                  <ul className="list-module">
                                    {module.content.whatYouCapture.map((item, i) => (
                                      <li key={i}>{item}</li>
                                    ))}
                                  </ul>
                                </>
                              )}
                              
                              {module.content.capabilities && (
                                <ul className="list-module">
                                  {module.content.capabilities.map((cap, i) => (
                                    <li key={i}>{cap}</li>
                                  ))}
                                </ul>
                              )}
                              
                              {module.content.features && (
                                <ul className="list-module">
                                  {module.content.features.map((feature, i) => (
                                    <li key={i}>{feature}</li>
                                  ))}
                                </ul>
                              )}
                              
                            </div>
                            
                            <div>
                              {/* Right column content */}
                              {module.content.tip && (
                                <div className="note-box-module">
                                  <strong>Tip:</strong> {module.content.tip}
                                </div>
                              )}
                              
                              {module.content.example && (
                                <div className="note-box-module">
                                  <strong>Example:</strong> {module.content.example}
                                </div>
                              )}
                              
                              {module.content.outcome && (
                                <div className="note-box-module">
                                  <strong>Outcome:</strong> {module.content.outcome}
                                </div>
                              )}
                              
                              {module.content.note && (
                                <div className="note-box-module" style={{ marginTop: module.content.tip || module.content.example || module.content.outcome ? '0.5rem' : '0' }}>
                                  <strong>Note:</strong> {module.content.note}
                                </div>
                              )}
                              
                              {module.content.examples && (
                                <p className="muted-module" style={{ marginTop: '0.5rem' }}>
                                  Typical examples: {module.content.examples.map((ex, i) => (
                                    <span key={i}>"{ex}"{i < module.content.examples.length - 1 ? ', ' : ''}</span>
                                  ))}
                                </p>
                              )}
                              
                              {module.content.progressBars && (
                                <>
                                  <p className="muted-module" style={{ marginTop: '0.5rem' }}>Example visual: high-level milestone completion.</p>
                                  <div className="bar-chart">
                                    {module.content.progressBars.map((bar, i) => (
                                      <motion.div
                                        key={i}
                                        className="bar-row"
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: i * 0.1 }}
                                      >
                                        <div className="bar-label">{bar.label}</div>
                                        <div className="bar-track">
                                          <motion.div
                                            className="bar-fill"
                                            initial={{ width: 0 }}
                                            whileInView={{ width: `${bar.percent}%` }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.8, delay: i * 0.1 + 0.2 }}
                                          />
                                        </div>
                                        <div className="bar-percent">{bar.percent}%</div>
                                      </motion.div>
                                    ))}
                                  </div>
                                </>
                              )}
                              
                              {module.content.chartData && (
                                <>
                                  <p className="muted-module" style={{ marginTop: '0.5rem' }}>Illustrative mini-chart: spend vs budget.</p>
                                  <div className="bar-chart">
                                    {module.content.chartData.map((bar, i) => (
                                      <motion.div
                                        key={i}
                                        className="bar-row"
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: i * 0.1 }}
                                      >
                                        <div className="bar-label">{bar.label}</div>
                                        <div className="bar-track">
                                          <motion.div
                                            className="bar-fill"
                                            initial={{ width: 0 }}
                                            whileInView={{ width: `${bar.percent}%` }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.8, delay: i * 0.1 + 0.2 }}
                                          />
                                        </div>
                                        <div className="bar-percent">{bar.percent}%</div>
                                      </motion.div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {module.content.statusColors && (
                            <div className="status-colors">
                              <h4>Status Colors:</h4>
                              <div className="colors-grid">
                                {Object.entries(module.content.statusColors).map(([status, color]) => (
                                  <div key={status} className="color-item">
                                    <div className="color-dot" style={{ backgroundColor: color }} />
                                    <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {module.content.lifecycle && (
                            <div className="lifecycle-timeline">
                              <h4>Contract Lifecycle:</h4>
                              <div className="timeline">
                                {module.content.lifecycle.map((stage, i) => (
                                  <motion.div
                                    key={i}
                                    className="timeline-item"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                  >
                                    <div className="timeline-dot" />
                                    <span>{stage}</span>
                                    {i < module.content.lifecycle.length - 1 && <div className="timeline-line" />}
                                  </motion.div>
                                ))}
                              </div>
                            </div>
                          )}
                          {module.content.features && (
                            <div className="module-features">
                              <h4>Key Features:</h4>
                              <ul>
                                {module.content.features.map((feature, i) => (
                                  <motion.li
                                    key={i}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                  >
                                    <CheckCircle size={16} />
                                    {feature}
                                  </motion.li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div className="module-placeholder">
                            <p className="placeholder-text">📸 Screenshot placeholder for {module.title} module</p>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </motion.div>
                );
              })}
            </div>
          </SectionContainer>

          {/* Journey Map Section */}
          <SectionContainer id="journey-map" title="End-to-End Journey Map">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="section-intro">
                Follow this step-by-step journey to see how users interact with CAM360 from project 
                creation to final closure.
              </p>

              <div className="journey-map">
                {journeySteps.map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <motion.div
                      key={step.step}
                      className="journey-step"
                      initial={{ opacity: 0, y: 50 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      whileHover={{ scale: 1.05, y: -5 }}
                    >
                      <div className="journey-number">{step.step}</div>
                      <div className="journey-icon-wrapper">
                        <Icon size={28} />
                      </div>
                      <h4>{step.title}</h4>
                      <p>{step.description}</p>
                      {index < journeySteps.length - 1 && (
                        <motion.div
                          className="journey-connector"
                          initial={{ scaleY: 0 }}
                          whileInView={{ scaleY: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                        />
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </SectionContainer>

          {/* Glossary Section */}
          <SectionContainer id="glossary" title="Glossary of Terms">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="section-intro">
                Understanding CAM360 terminology helps you get the most out of the platform.
              </p>

              <div className="glossary-grid">
                {glossaryTerms.map((term, index) => (
                  <motion.div
                    key={term.term}
                    className="glossary-card"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    whileHover={{ scale: 1.03, y: -4 }}
                  >
                    <h4>{term.term}</h4>
                    <p>{term.definition}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </SectionContainer>

          {/* FAQ Section */}
          <SectionContainer id="faq" title="Frequently Asked Questions">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="faq-list">
                {faqItems.map((item) => {
                  const isExpanded = expandedFAQ === item.id;
                  return (
                    <motion.div
                      key={item.id}
                      className="faq-item"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4 }}
                    >
                      <button
                        className="faq-question"
                        onClick={() => setExpandedFAQ(isExpanded ? null : item.id)}
                      >
                        <span>{item.question}</span>
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </button>
                      <motion.div
                        className="faq-answer"
                        initial={false}
                        animate={{
                          height: isExpanded ? 'auto' : 0,
                          opacity: isExpanded ? 1 : 0
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        {isExpanded && <p>{item.answer}</p>}
                      </motion.div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </SectionContainer>

          {/* Pharma Industry Example Section */}
          <SectionContainer id="pharma-example" title="Industry Example: Pharmaceutical Company">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="pharma-example">
                <div className="example-intro">
                  <h3>Real-World Use Case: PharmaCorp's New Drug Development Project</h3>
                  <p className="lead">
                    PharmaCorp, a leading pharmaceutical company, uses CAM360 to manage a complex 
                    3-year drug development project from research to FDA approval. Here's their complete workflow.
                  </p>
                </div>

                {/* Project Setup */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="phase-number">1</div>
                    <div>
                      <h4>Project Creation: "OncoCure - Cancer Treatment Development"</h4>
                      <p className="phase-subtitle">Initial Setup & Planning</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="phase-details">
                      <div className="detail-item">
                        <strong>Project Details:</strong>
                        <ul>
                          <li><strong>Timeline:</strong> 36 months (3 years)</li>
                          <li><strong>Budget:</strong> $150 million allocated across phases</li>
                          <li><strong>Team:</strong> 45 internal staff + 12 external contractors</li>
                          <li><strong>Objective:</strong> Develop and gain FDA approval for new oncology drug</li>
                        </ul>
                      </div>
                      <div className="detail-item">
                        <strong>Initial Setup in CAM360:</strong>
                        <ul>
                          <li>Created project with comprehensive metadata (drug name, indication, target market)</li>
                          <li>Set up financial tracking with multi-currency support (USD, EUR for EU partners)</li>
                          <li>Configured compliance workflows for FDA and EMA requirements</li>
                          <li>Integrated with existing ERP system for budget allocation</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Milestones */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    <div className="phase-number">2</div>
                    <div>
                      <h4>Milestone Definition: Critical Path Planning</h4>
                      <p className="phase-subtitle">Breaking Down the Development Journey</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="milestones-grid">
                      {[
                        { name: 'Pre-Clinical Research', duration: 'Months 1-6', status: 'completed', color: '#3BAA72' },
                        { name: 'IND Submission', duration: 'Month 7', status: 'completed', color: '#3BAA72' },
                        { name: 'Phase I Clinical Trials', duration: 'Months 8-14', status: 'inProgress', color: '#4678be' },
                        { name: 'Phase II Clinical Trials', duration: 'Months 15-24', status: 'pending', color: '#687484' },
                        { name: 'Phase III Clinical Trials', duration: 'Months 25-32', status: 'pending', color: '#687484' },
                        { name: 'NDA Submission', duration: 'Month 33', status: 'pending', color: '#687484' },
                        { name: 'FDA Approval', duration: 'Month 36', status: 'pending', color: '#687484' }
                      ].map((milestone, index) => (
                        <motion.div
                          key={index}
                          className="milestone-card"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          whileHover={{ scale: 1.02, y: -4 }}
                        >
                          <div className="milestone-status" style={{ backgroundColor: `${milestone.color}20`, borderColor: milestone.color }}>
                            <div className="status-dot" style={{ backgroundColor: milestone.color }} />
                          </div>
                          <h5>{milestone.name}</h5>
                          <p>{milestone.duration}</p>
                          <span className="status-badge" style={{ color: milestone.color }}>
                            {milestone.status === 'completed' ? '✓ Completed' : 
                             milestone.status === 'inProgress' ? '→ In Progress' : 
                             '○ Pending'}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                    <div className="phase-note">
                      <Info size={18} />
                      <p>
                        <strong>AI Insight:</strong> CAM360's AI detected that Phase I completion is tracking 2 weeks ahead of schedule, 
                        allowing PharmaCorp to proactively prepare Phase II resources earlier than planned.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Subcontractors */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <div className="phase-number">3</div>
                    <div>
                      <h4>Subcontractor Management: External Partners</h4>
                      <p className="phase-subtitle">Coordinating with CROs, Labs, and Regulatory Consultants</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="subcontractors-list">
                      {[
                        {
                          name: 'Clinical Research Organization (CRO) - MedTrials Inc.',
                          role: 'Phase I-III Clinical Trial Management',
                          contract: '$45M over 24 months',
                          milestones: ['Phase I Trials', 'Phase II Trials', 'Phase III Trials'],
                          performance: 'Excellent - 98% on-time delivery'
                        },
                        {
                          name: 'Regulatory Affairs Consultant - RegCompliance LLC',
                          role: 'FDA/EMA Submission Support',
                          contract: '$8M for regulatory filings',
                          milestones: ['IND Submission', 'NDA Submission'],
                          performance: 'On Track - All deadlines met'
                        },
                        {
                          name: 'Manufacturing Partner - BioPharm Manufacturing',
                          role: 'Drug Substance Production',
                          contract: '$32M for clinical supply',
                          milestones: ['Pre-Clinical Supply', 'Phase I Supply', 'Phase II Supply'],
                          performance: 'Good - Minor delays in Phase I supply resolved'
                        },
                        {
                          name: 'Laboratory Services - LabTech Analytics',
                          role: 'Bioanalytical Testing & Data Analysis',
                          contract: '$12M for testing services',
                          milestones: ['Pre-Clinical Testing', 'Clinical Sample Analysis'],
                          performance: 'Excellent - Fast turnaround times'
                        }
                      ].map((vendor, index) => (
                        <motion.div
                          key={index}
                          className="vendor-card"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          whileHover={{ boxShadow: '0 8px 16px rgba(70, 120, 190, 0.15)' }}
                        >
                          <div className="vendor-header">
                            <Building2 size={20} style={{ color: '#4678be' }} />
                            <div>
                              <h5>{vendor.name}</h5>
                              <p className="vendor-role">{vendor.role}</p>
                            </div>
                          </div>
                          <div className="vendor-details">
                            <div className="vendor-detail-item">
                              <strong>Contract Value:</strong> {vendor.contract}
                            </div>
                            <div className="vendor-detail-item">
                              <strong>Linked Milestones:</strong>
                              <div className="milestone-tags">
                                {vendor.milestones.map((m, i) => (
                                  <span key={i} className="milestone-tag">{m}</span>
                                ))}
                              </div>
                            </div>
                            <div className="vendor-detail-item">
                              <strong>Performance:</strong> 
                              <span className="performance-badge" style={{ 
                                color: vendor.performance.includes('Excellent') ? '#3BAA72' : 
                                       vendor.performance.includes('On Track') ? '#4678be' : '#F2B740'
                              }}>
                                {vendor.performance}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Contracts */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    <div className="phase-number">4</div>
                    <div>
                      <h4>Contract Lifecycle: From Draft to Execution</h4>
                      <p className="phase-subtitle">Managing Legal Agreements with Multiple Parties</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="contract-timeline-example">
                      {[
                        { stage: 'Draft', description: 'Legal team creates initial contract templates', contracts: 4 },
                        { stage: 'Review', description: 'Internal review by finance, compliance, and project teams', contracts: 4 },
                        { stage: 'Negotiation', description: 'Back-and-forth with vendors on terms and pricing', contracts: 3 },
                        { stage: 'Approval', description: 'Final approval from CFO and project sponsor', contracts: 4 },
                        { stage: 'Signed', description: 'E-signature execution by all parties', contracts: 4 },
                        { stage: 'Active', description: 'Contracts in execution with payment milestones', contracts: 4 }
                      ].map((stage, index) => (
                        <motion.div
                          key={index}
                          className="contract-stage"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                        >
                          <div className="stage-indicator" />
                          <div className="stage-content">
                            <h5>{stage.stage}</h5>
                            <p>{stage.description}</p>
                            <span className="contract-count">{stage.contracts} contracts</span>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                    <div className="phase-note">
                      <FileText size={18} />
                      <p>
                        <strong>Key Contracts:</strong> CRO Master Service Agreement ($45M), Manufacturing Agreement ($32M), 
                        Regulatory Consulting Agreement ($8M), and Laboratory Services Agreement ($12M). 
                        All contracts linked to specific milestones for automatic payment triggers.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Assets */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    <div className="phase-number">5</div>
                    <div>
                      <h4>Asset Allocation: Equipment & Facilities</h4>
                      <p className="phase-subtitle">Tracking Lab Equipment, Manufacturing Assets, and IT Infrastructure</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="assets-grid">
                      {[
                        {
                          name: 'HPLC Systems',
                          category: 'Laboratory Equipment',
                          quantity: 8,
                          value: '$2.4M',
                          utilization: '85%',
                          assignedTo: 'Pre-Clinical & Clinical Testing',
                          maintenance: 'Scheduled quarterly'
                        },
                        {
                          name: 'GMP Manufacturing Line',
                          category: 'Production Equipment',
                          quantity: 2,
                          value: '$18M',
                          utilization: '72%',
                          assignedTo: 'Clinical Supply Manufacturing',
                          maintenance: 'Preventive maintenance active'
                        },
                        {
                          name: 'Clinical Data Management System',
                          category: 'IT Infrastructure',
                          quantity: 1,
                          value: '$850K',
                          utilization: '100%',
                          assignedTo: 'All Clinical Phases',
                          maintenance: 'Cloud-based, auto-updates'
                        },
                        {
                          name: 'Cold Storage Facilities',
                          category: 'Facilities',
                          quantity: 3,
                          value: '$5.2M',
                          utilization: '68%',
                          assignedTo: 'Drug Substance Storage',
                          maintenance: '24/7 monitoring'
                        }
                      ].map((asset, index) => (
                        <motion.div
                          key={index}
                          className="asset-card-example"
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          whileHover={{ scale: 1.02, y: -4 }}
                        >
                          <Wrench size={24} style={{ color: '#4678be', marginBottom: '0.5rem' }} />
                          <h5>{asset.name}</h5>
                          <p className="asset-category">{asset.category}</p>
                          <div className="asset-metrics">
                            <div className="metric">
                              <strong>Value:</strong> {asset.value}
                            </div>
                            <div className="metric">
                              <strong>Utilization:</strong> 
                              <span style={{ color: asset.utilization === '100%' ? '#3BAA72' : '#4678be' }}>
                                {asset.utilization}
                              </span>
                            </div>
                            <div className="metric">
                              <strong>Assigned:</strong> {asset.assignedTo}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Finance */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    <div className="phase-number">6</div>
                    <div>
                      <h4>Financial Tracking: Budget vs Actual</h4>
                      <p className="phase-subtitle">Real-Time Cost Monitoring Across All Project Phases</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="finance-overview">
                      <div className="finance-card">
                        <h5>Total Budget</h5>
                        <div className="finance-amount">$150,000,000</div>
                        <p className="finance-label">Allocated over 36 months</p>
                      </div>
                      <div className="finance-card">
                        <h5>Actual Spend (Month 14)</h5>
                        <div className="finance-amount" style={{ color: '#3BAA72' }}>$42,300,000</div>
                        <p className="finance-label">28% of total budget</p>
                      </div>
                      <div className="finance-card">
                        <h5>Budgeted (Month 14)</h5>
                        <div className="finance-amount">$44,500,000</div>
                        <p className="finance-label">Expected at this point</p>
                      </div>
                      <div className="finance-card highlight">
                        <h5>Variance</h5>
                        <div className="finance-amount" style={{ color: '#3BAA72' }}>-$2,200,000</div>
                        <p className="finance-label">Under budget (5% savings)</p>
                      </div>
                    </div>
                    <div className="spend-breakdown">
                      <h5>Spend by Category (Year 1)</h5>
                      <div className="spend-items">
                        {[
                          { category: 'CRO Services', amount: '$18.5M', percentage: 44, color: '#4678be' },
                          { category: 'Manufacturing', amount: '$12.2M', percentage: 29, color: '#3BAA72' },
                          { category: 'Laboratory Testing', amount: '$5.8M', percentage: 14, color: '#F2B740' },
                          { category: 'Regulatory', amount: '$3.2M', percentage: 8, color: '#8B5CF6' },
                          { category: 'Internal Resources', amount: '$2.6M', percentage: 6, color: '#E57373' }
                        ].map((item, index) => (
                          <motion.div
                            key={index}
                            className="spend-item"
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                          >
                            <div className="spend-header">
                              <span>{item.category}</span>
                              <strong>{item.amount}</strong>
                            </div>
                            <div className="spend-bar">
                              <motion.div
                                className="spend-bar-fill"
                                style={{ backgroundColor: item.color }}
                                initial={{ width: 0 }}
                                whileInView={{ width: `${item.percentage}%` }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: index * 0.1 }}
                              />
                            </div>
                            <span className="spend-percentage">{item.percentage}%</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Intelligence */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                  >
                    <div className="phase-number">7</div>
                    <div>
                      <h4>AI Intelligence: Smart Insights & Predictions</h4>
                      <p className="phase-subtitle">How CAM360's AI Helps PharmaCorp Stay Ahead</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="ai-insights-grid">
                      {[
                        {
                          type: 'Risk Detection',
                          title: 'Early Warning: Potential Phase II Delay',
                          description: 'AI analyzed historical data and detected that Phase I completion patterns suggest Phase II may face 3-week delay due to patient recruitment challenges.',
                          action: 'Recommended: Start patient recruitment 4 weeks early',
                          impact: 'Prevented $2.1M in delay costs',
                          icon: AlertTriangle,
                          color: '#F2B740'
                        },
                        {
                          type: 'Budget Prediction',
                          title: 'Cost Forecast: Under Budget Trend',
                          description: 'AI predicts project will complete 6% under budget ($9M savings) based on current spending patterns and milestone completion rates.',
                          action: 'Suggested: Allocate savings to accelerate Phase III',
                          impact: 'Potential 2-month acceleration',
                          icon: TrendingDown,
                          color: '#3BAA72'
                        },
                        {
                          type: 'Contract Optimization',
                          title: 'Vendor Performance Analysis',
                          description: 'AI identified that LabTech Analytics consistently delivers 15% faster than industry average, enabling faster decision-making.',
                          action: 'Recommended: Increase LabTech allocation for Phase II',
                          impact: 'Improved timeline efficiency',
                          icon: BarChart3,
                          color: '#4678be'
                        },
                        {
                          type: 'Compliance Alert',
                          title: 'FDA Submission Readiness Check',
                          description: 'AI reviewed all documentation and flagged 3 missing compliance documents needed for NDA submission 8 months ahead of deadline.',
                          action: 'Automated workflow created for document collection',
                          impact: 'Ensured on-time submission',
                          icon: Shield,
                          color: '#8B5CF6'
                        },
                        {
                          type: 'Resource Optimization',
                          title: 'Asset Utilization Recommendation',
                          description: 'AI detected that HPLC systems are underutilized (65% vs 85% target) and recommended reallocation to other projects.',
                          action: 'Suggested: Share 2 HPLC systems with parallel project',
                          impact: 'Improved ROI on equipment investment',
                          icon: Activity,
                          color: '#17a2b8'
                        },
                        {
                          type: 'Predictive Analytics',
                          title: 'Timeline Prediction: On Track for Early Approval',
                          description: 'AI analyzed milestone completion rates, vendor performance, and historical pharma project data to predict FDA approval 1 month early.',
                          action: 'Recommended: Prepare commercialization team early',
                          impact: 'Faster time-to-market = $15M additional revenue',
                          icon: Sparkles,
                          color: '#8B5CF6'
                        }
                      ].map((insight, index) => {
                        const Icon = insight.icon;
                        return (
                          <motion.div
                            key={index}
                            className="ai-insight-card"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                            whileHover={{ scale: 1.02, y: -4 }}
                          >
                            <div className="ai-insight-header" style={{ borderLeftColor: insight.color }}>
                              <Icon size={24} style={{ color: insight.color }} />
                              <div>
                                <span className="insight-type">{insight.type}</span>
                                <h5>{insight.title}</h5>
                              </div>
                            </div>
                            <p className="insight-description">{insight.description}</p>
                            <div className="insight-action">
                              <strong>Action Taken:</strong> {insight.action}
                            </div>
                            <div className="insight-impact" style={{ backgroundColor: `${insight.color}15` }}>
                              <strong>Impact:</strong> {insight.impact}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Collaboration */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                  >
                    <div className="phase-number">8</div>
                    <div>
                      <h4>Collaboration: Team Communication & Document Sharing</h4>
                      <p className="phase-subtitle">Keeping 45 Internal Staff + 12 External Partners Aligned</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="collaboration-stats">
                      <div className="stat-card">
                        <MessageSquare size={24} style={{ color: '#4678be' }} />
                        <div className="stat-value">1,247</div>
                        <div className="stat-label">Comments & Discussions</div>
                      </div>
                      <div className="stat-card">
                        <FileText size={24} style={{ color: '#4678be' }} />
                        <div className="stat-value">342</div>
                        <div className="stat-label">Documents Shared</div>
                      </div>
                      <div className="stat-card">
                        <Users size={24} style={{ color: '#4678be' }} />
                        <div className="stat-value">57</div>
                        <div className="stat-label">Active Team Members</div>
                      </div>
                      <div className="stat-card">
                        <CheckCircle size={24} style={{ color: '#3BAA72' }} />
                        <div className="stat-value">98%</div>
                        <div className="stat-label">Response Rate</div>
                      </div>
                    </div>
                    <div className="collaboration-examples">
                      <h5>Key Collaboration Scenarios:</h5>
                      <div className="example-scenarios">
                        {[
                          {
                            scenario: 'Clinical Data Review',
                            description: 'Phase I data review meeting with 12 participants from CRO, internal team, and regulatory consultant. All documents shared in CAM360, decisions documented, and action items tracked.',
                            participants: '12 team members',
                            documents: '45 files shared'
                          },
                          {
                            scenario: 'Budget Approval Workflow',
                            description: 'CFO @mentioned in budget discussion. All financial documents, vendor quotes, and cost analysis shared. Approval granted with comments and conditions documented.',
                            participants: '8 stakeholders',
                            documents: '23 financial documents'
                          },
                          {
                            scenario: 'Regulatory Submission Prep',
                            description: 'Real-time collaboration on NDA submission documents. Version control maintained, all team members notified of changes, and compliance checklist tracked.',
                            participants: '15 team members',
                            documents: '127 regulatory documents'
                          }
                        ].map((example, index) => (
                          <motion.div
                            key={index}
                            className="scenario-card"
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                          >
                            <h6>{example.scenario}</h6>
                            <p>{example.description}</p>
                            <div className="scenario-metrics">
                              <span><Users size={14} /> {example.participants}</span>
                              <span><FileText size={14} /> {example.documents}</span>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Results Summary */}
                <div className="example-results">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                  >
                    <h3>Results & Impact</h3>
                    <div className="results-grid">
                      <div className="result-item">
                        <CheckCircle size={32} style={{ color: '#3BAA72' }} />
                        <h4>5% Under Budget</h4>
                        <p>$7.5M saved through better cost tracking and AI recommendations</p>
                      </div>
                      <div className="result-item">
                        <Clock size={32} style={{ color: '#4678be' }} />
                        <h4>On Track Timeline</h4>
                        <p>All milestones met on time with AI early warning system preventing delays</p>
                      </div>
                      <div className="result-item">
                        <TrendingUp size={32} style={{ color: '#3BAA72' }} />
                        <h4>98% Team Satisfaction</h4>
                        <p>Improved collaboration and visibility across all stakeholders</p>
                      </div>
                      <div className="result-item">
                        <Shield size={32} style={{ color: '#8B5CF6' }} />
                        <h4>100% Compliance</h4>
                        <p>All regulatory requirements met with automated compliance tracking</p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </SectionContainer>

          {/* Real Estate Industry Example Section */}
          <SectionContainer id="real-estate-example" title="Industry Example: Real Estate Development Company">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="pharma-example">
                <div className="example-intro">
                  <h3>Real-World Use Case: PrimeReal Estate's Mixed-Use Development Project</h3>
                  <p className="lead">
                    PrimeReal Estate, a leading commercial real estate developer, uses CAM360 to manage 
                    a $250M mixed-use development project combining residential, retail, and office spaces 
                    over 24 months. Here's their complete workflow from land acquisition to tenant occupancy.
                  </p>
                </div>

                {/* Project Setup */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="phase-number">1</div>
                    <div>
                      <h4>Project Creation: "Downtown Plaza - Mixed-Use Complex"</h4>
                      <p className="phase-subtitle">Initial Setup & Planning</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="phase-details">
                      <div className="detail-item">
                        <strong>Project Details:</strong>
                        <ul>
                          <li><strong>Timeline:</strong> 24 months (2 years)</li>
                          <li><strong>Budget:</strong> $250 million total project cost</li>
                          <li><strong>Team:</strong> 38 internal staff + 25 external contractors</li>
                          <li><strong>Scope:</strong> 500 residential units, 150,000 sq ft retail, 200,000 sq ft office space</li>
                          <li><strong>Location:</strong> Downtown metropolitan area</li>
                        </ul>
                      </div>
                      <div className="detail-item">
                        <strong>Initial Setup in CAM360:</strong>
                        <ul>
                          <li>Created project with zoning, permits, and regulatory requirements</li>
                          <li>Set up multi-phase financial tracking (Pre-Construction, Construction, Post-Construction)</li>
                          <li>Configured compliance workflows for building codes, environmental regulations</li>
                          <li>Integrated with property management system for future tenant tracking</li>
                          <li>Linked to CRM for pre-leasing and sales activities</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Milestones */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    <div className="phase-number">2</div>
                    <div>
                      <h4>Milestone Definition: Construction Phases</h4>
                      <p className="phase-subtitle">Breaking Down the Development Timeline</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="milestones-grid">
                      {[
                        { name: 'Land Acquisition & Due Diligence', duration: 'Months 1-2', status: 'completed', color: '#3BAA72' },
                        { name: 'Design & Permits', duration: 'Months 3-6', status: 'completed', color: '#3BAA72' },
                        { name: 'Site Preparation & Foundation', duration: 'Months 7-9', status: 'inProgress', color: '#4678be' },
                        { name: 'Structural Construction', duration: 'Months 10-16', status: 'pending', color: '#687484' },
                        { name: 'MEP & Interior Fit-Out', duration: 'Months 17-20', status: 'pending', color: '#687484' },
                        { name: 'Exterior & Landscaping', duration: 'Months 21-22', status: 'pending', color: '#687484' },
                        { name: 'Final Inspections & Occupancy', duration: 'Months 23-24', status: 'pending', color: '#687484' }
                      ].map((milestone, index) => (
                        <motion.div
                          key={index}
                          className="milestone-card"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          whileHover={{ scale: 1.02, y: -4 }}
                        >
                          <div className="milestone-status" style={{ backgroundColor: `${milestone.color}20`, borderColor: milestone.color }}>
                            <div className="status-dot" style={{ backgroundColor: milestone.color }} />
                          </div>
                          <h5>{milestone.name}</h5>
                          <p>{milestone.duration}</p>
                          <span className="status-badge" style={{ color: milestone.color }}>
                            {milestone.status === 'completed' ? '✓ Completed' : 
                             milestone.status === 'inProgress' ? '→ In Progress' : 
                             '○ Pending'}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                    <div className="phase-note">
                      <Info size={18} />
                      <p>
                        <strong>AI Insight:</strong> CAM360's AI analyzed weather patterns and historical construction data 
                        to recommend starting foundation work 3 weeks earlier than planned, avoiding potential winter delays 
                        that could cost $1.8M in extended project timeline.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Subcontractors */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <div className="phase-number">3</div>
                    <div>
                      <h4>Subcontractor Management: Construction Partners</h4>
                      <p className="phase-subtitle">Coordinating with General Contractors, Subcontractors, and Vendors</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="subcontractors-list">
                      {[
                        {
                          name: 'General Contractor - BuildCorp Construction',
                          role: 'Overall Construction Management & Coordination',
                          contract: '$180M over 20 months',
                          milestones: ['Site Preparation', 'Structural Construction', 'MEP Installation', 'Final Inspections'],
                          performance: 'Excellent - 99% on-time delivery, zero safety incidents'
                        },
                        {
                          name: 'Architectural Firm - DesignWorks Associates',
                          role: 'Design Development & Construction Documents',
                          contract: '$12M for design services',
                          milestones: ['Design & Permits', 'Construction Administration'],
                          performance: 'Excellent - All designs approved on first submission'
                        },
                        {
                          name: 'MEP Contractor - SystemsPro Engineering',
                          role: 'Mechanical, Electrical & Plumbing Installation',
                          contract: '$28M for MEP systems',
                          milestones: ['MEP & Interior Fit-Out'],
                          performance: 'On Track - Ahead of schedule by 2 weeks'
                        },
                        {
                          name: 'Landscaping & Exterior - GreenScape Solutions',
                          role: 'Exterior Finishes, Hardscaping & Landscaping',
                          contract: '$8M for exterior work',
                          milestones: ['Exterior & Landscaping'],
                          performance: 'Scheduled - Materials ordered, ready to start'
                        },
                        {
                          name: 'Specialty Subcontractors - Various',
                          role: 'Elevator, Security, HVAC, Fire Safety Systems',
                          contract: '$15M combined for specialty systems',
                          milestones: ['MEP & Interior Fit-Out', 'Final Inspections'],
                          performance: 'On Track - All vendors coordinated'
                        }
                      ].map((vendor, index) => (
                        <motion.div
                          key={index}
                          className="vendor-card"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          whileHover={{ boxShadow: '0 8px 16px rgba(70, 120, 190, 0.15)' }}
                        >
                          <div className="vendor-header">
                            <Building2 size={20} style={{ color: '#4678be' }} />
                            <div>
                              <h5>{vendor.name}</h5>
                              <p className="vendor-role">{vendor.role}</p>
                            </div>
                          </div>
                          <div className="vendor-details">
                            <div className="vendor-detail-item">
                              <strong>Contract Value:</strong> {vendor.contract}
                            </div>
                            <div className="vendor-detail-item">
                              <strong>Linked Milestones:</strong>
                              <div className="milestone-tags">
                                {vendor.milestones.map((m, i) => (
                                  <span key={i} className="milestone-tag">{m}</span>
                                ))}
                              </div>
                            </div>
                            <div className="vendor-detail-item">
                              <strong>Performance:</strong> 
                              <span className="performance-badge" style={{ 
                                color: vendor.performance.includes('Excellent') ? '#3BAA72' : 
                                       vendor.performance.includes('On Track') ? '#4678be' : '#F2B740'
                              }}>
                                {vendor.performance}
                              </span>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Contracts */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    <div className="phase-number">4</div>
                    <div>
                      <h4>Contract Lifecycle: Construction & Service Agreements</h4>
                      <p className="phase-subtitle">Managing Complex Multi-Party Contracts</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="contract-timeline-example">
                      {[
                        { stage: 'Draft', description: 'Legal team creates contracts with payment schedules tied to milestones', contracts: 8 },
                        { stage: 'Review', description: 'Review by project manager, finance, and risk management teams', contracts: 8 },
                        { stage: 'Negotiation', description: 'Price negotiations, change orders, and terms finalization', contracts: 6 },
                        { stage: 'Approval', description: 'Final approval from CFO, project sponsor, and board', contracts: 8 },
                        { stage: 'Signed', description: 'E-signature execution with all contractors and vendors', contracts: 8 },
                        { stage: 'Active', description: 'Contracts in execution with milestone-based payments', contracts: 8 }
                      ].map((stage, index) => (
                        <motion.div
                          key={index}
                          className="contract-stage"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                        >
                          <div className="stage-indicator" />
                          <div className="stage-content">
                            <h5>{stage.stage}</h5>
                            <p>{stage.description}</p>
                            <span className="contract-count">{stage.contracts} contracts</span>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                    <div className="phase-note">
                      <FileText size={18} />
                      <p>
                        <strong>Key Contracts:</strong> General Contractor Agreement ($180M), Architectural Services ($12M), 
                        MEP Installation ($28M), Landscaping & Exterior ($8M), and 4 specialty system contracts ($15M combined). 
                        All contracts include milestone-based payment schedules, performance bonds, and liquidated damages clauses.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Assets */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    <div className="phase-number">5</div>
                    <div>
                      <h4>Asset Allocation: Construction Equipment & Facilities</h4>
                      <p className="phase-subtitle">Tracking Heavy Machinery, Tools, and Temporary Facilities</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="assets-grid">
                      {[
                        {
                          name: 'Tower Cranes',
                          category: 'Heavy Equipment',
                          quantity: 3,
                          value: '$4.2M',
                          utilization: '100%',
                          assignedTo: 'Structural Construction Phase',
                          maintenance: 'Daily inspections, monthly servicing'
                        },
                        {
                          name: 'Excavators & Earthmoving Equipment',
                          category: 'Construction Equipment',
                          quantity: 12,
                          value: '$3.8M',
                          utilization: '95%',
                          assignedTo: 'Site Preparation & Foundation',
                          maintenance: 'Weekly maintenance schedule'
                        },
                        {
                          name: 'Temporary Site Offices & Facilities',
                          category: 'Temporary Structures',
                          quantity: 8,
                          value: '$450K',
                          utilization: '100%',
                          assignedTo: 'Entire Project Duration',
                          maintenance: 'Monthly cleaning & repairs'
                        },
                        {
                          name: 'Concrete Mixers & Batching Plants',
                          category: 'Construction Equipment',
                          quantity: 6,
                          value: '$1.2M',
                          utilization: '88%',
                          assignedTo: 'Foundation & Structural Work',
                          maintenance: 'Daily cleaning, weekly servicing'
                        },
                        {
                          name: 'Safety Equipment & PPE',
                          category: 'Safety Assets',
                          quantity: 500,
                          value: '$180K',
                          utilization: '100%',
                          assignedTo: 'All Construction Phases',
                          maintenance: 'Regular replacement & inspection'
                        },
                        {
                          name: 'Surveying & Measurement Equipment',
                          category: 'Technical Equipment',
                          quantity: 15,
                          value: '$320K',
                          utilization: '92%',
                          assignedTo: 'Site Preparation & Quality Control',
                          maintenance: 'Calibrated monthly'
                        }
                      ].map((asset, index) => (
                        <motion.div
                          key={index}
                          className="asset-card-example"
                          initial={{ opacity: 0, scale: 0.9 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          whileHover={{ scale: 1.02, y: -4 }}
                        >
                          <Wrench size={24} style={{ color: '#4678be', marginBottom: '0.5rem' }} />
                          <h5>{asset.name}</h5>
                          <p className="asset-category">{asset.category}</p>
                          <div className="asset-metrics">
                            <div className="metric">
                              <strong>Value:</strong> {asset.value}
                            </div>
                            <div className="metric">
                              <strong>Utilization:</strong> 
                              <span style={{ color: asset.utilization === '100%' ? '#3BAA72' : '#4678be' }}>
                                {asset.utilization}
                              </span>
                            </div>
                            <div className="metric">
                              <strong>Assigned:</strong> {asset.assignedTo}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Finance */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                  >
                    <div className="phase-number">6</div>
                    <div>
                      <h4>Financial Tracking: Budget vs Actual</h4>
                      <p className="phase-subtitle">Real-Time Cost Monitoring Across Construction Phases</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="finance-overview">
                      <div className="finance-card">
                        <h5>Total Budget</h5>
                        <div className="finance-amount">$250,000,000</div>
                        <p className="finance-label">Allocated over 24 months</p>
                      </div>
                      <div className="finance-card">
                        <h5>Actual Spend (Month 9)</h5>
                        <div className="finance-amount" style={{ color: '#3BAA72' }}>$78,500,000</div>
                        <p className="finance-label">31% of total budget</p>
                      </div>
                      <div className="finance-card">
                        <h5>Budgeted (Month 9)</h5>
                        <div className="finance-amount">$82,000,000</div>
                        <p className="finance-label">Expected at this point</p>
                      </div>
                      <div className="finance-card highlight">
                        <h5>Variance</h5>
                        <div className="finance-amount" style={{ color: '#3BAA72' }}>-$3,500,000</div>
                        <p className="finance-label">Under budget (4.3% savings)</p>
                      </div>
                    </div>
                    <div className="spend-breakdown">
                      <h5>Spend by Category (First 9 Months)</h5>
                      <div className="spend-items">
                        {[
                          { category: 'General Contractor', amount: '$52.5M', percentage: 67, color: '#4678be' },
                          { category: 'Land & Site Acquisition', amount: '$18.2M', percentage: 23, color: '#3BAA72' },
                          { category: 'Design & Permits', amount: '$4.8M', percentage: 6, color: '#F2B740' },
                          { category: 'Equipment & Materials', amount: '$2.1M', percentage: 3, color: '#8B5CF6' },
                          { category: 'Project Management', amount: '$900K', percentage: 1, color: '#E57373' }
                        ].map((item, index) => (
                          <motion.div
                            key={index}
                            className="spend-item"
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                          >
                            <div className="spend-header">
                              <span>{item.category}</span>
                              <strong>{item.amount}</strong>
                            </div>
                            <div className="spend-bar">
                              <motion.div
                                className="spend-bar-fill"
                                style={{ backgroundColor: item.color }}
                                initial={{ width: 0 }}
                                whileInView={{ width: `${item.percentage}%` }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: index * 0.1 }}
                              />
                            </div>
                            <span className="spend-percentage">{item.percentage}%</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                    <div className="phase-note">
                      <DollarSign size={18} />
                      <p>
                        <strong>Revenue Projections:</strong> Pre-leasing commitments total $45M in future revenue. 
                        CAM360 tracks both construction costs and future income streams, providing complete financial visibility.
                      </p>
                    </div>
                  </div>
                </div>

                {/* AI Intelligence */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                  >
                    <div className="phase-number">7</div>
                    <div>
                      <h4>AI Intelligence: Smart Insights for Real Estate Development</h4>
                      <p className="phase-subtitle">How CAM360's AI Helps PrimeReal Estate Optimize Operations</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="ai-insights-grid">
                      {[
                        {
                          type: 'Risk Detection',
                          title: 'Weather Delay Prediction',
                          description: 'AI analyzed 10-year weather patterns and predicted potential foundation delays due to heavy rainfall in months 8-9. Recommended starting foundation work 3 weeks early.',
                          action: 'Action Taken: Adjusted schedule, started foundation prep early',
                          impact: 'Avoided $1.8M in delay costs and potential liquidated damages',
                          icon: AlertTriangle,
                          color: '#F2B740'
                        },
                        {
                          type: 'Cost Optimization',
                          title: 'Material Price Forecasting',
                          description: 'AI detected rising steel prices trend and recommended locking in prices 4 months early through forward contracts, before 12% price increase.',
                          action: 'Action Taken: Executed forward contracts for steel and concrete',
                          impact: 'Saved $2.4M in material costs',
                          icon: TrendingDown,
                          color: '#3BAA72'
                        },
                        {
                          type: 'Schedule Optimization',
                          title: 'Critical Path Analysis',
                          description: 'AI identified that MEP installation could start 2 weeks earlier if structural work sequence was optimized, reducing overall timeline.',
                          action: 'Action Taken: Resequenced structural work, accelerated MEP start',
                          impact: 'Reduced project timeline by 3 weeks, earlier revenue generation',
                          icon: Clock,
                          color: '#4678be'
                        },
                        {
                          type: 'Compliance Monitoring',
                          title: 'Building Code Compliance Check',
                          description: 'AI reviewed all construction documents against latest building codes and flagged 5 potential compliance issues 6 months before inspections.',
                          action: 'Action Taken: Updated designs proactively, ensured full compliance',
                          impact: 'Prevented costly rework and inspection delays',
                          icon: Shield,
                          color: '#8B5CF6'
                        },
                        {
                          type: 'Resource Allocation',
                          title: 'Equipment Utilization Optimization',
                          description: 'AI detected that tower cranes were underutilized during foundation phase and recommended sharing with nearby project, reducing rental costs.',
                          action: 'Action Taken: Shared crane resources with adjacent development',
                          impact: 'Reduced equipment costs by $180K',
                          icon: Activity,
                          color: '#17a2b8'
                        },
                        {
                          type: 'Predictive Analytics',
                          title: 'Market Timing Prediction',
                          description: 'AI analyzed market trends, pre-leasing velocity, and economic indicators to predict optimal completion timing for maximum tenant demand.',
                          action: 'Action Taken: Accelerated completion by 1 month to capture peak market',
                          impact: 'Increased pre-leasing by 15%, $6.75M additional revenue',
                          icon: Sparkles,
                          color: '#8B5CF6'
                        }
                      ].map((insight, index) => {
                        const Icon = insight.icon;
                        return (
                          <motion.div
                            key={index}
                            className="ai-insight-card"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                            whileHover={{ scale: 1.02, y: -4 }}
                          >
                            <div className="ai-insight-header" style={{ borderLeftColor: insight.color }}>
                              <Icon size={24} style={{ color: insight.color }} />
                              <div>
                                <span className="insight-type">{insight.type}</span>
                                <h5>{insight.title}</h5>
                              </div>
                            </div>
                            <p className="insight-description">{insight.description}</p>
                            <div className="insight-action">
                              <strong>Action Taken:</strong> {insight.action}
                            </div>
                            <div className="insight-impact" style={{ backgroundColor: `${insight.color}15` }}>
                              <strong>Impact:</strong> {insight.impact}
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Collaboration */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.7 }}
                  >
                    <div className="phase-number">8</div>
                    <div>
                      <h4>Collaboration: Multi-Party Coordination</h4>
                      <p className="phase-subtitle">Keeping 38 Internal Staff + 25 External Contractors Aligned</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="collaboration-stats">
                      <div className="stat-card">
                        <MessageSquare size={24} style={{ color: '#4678be' }} />
                        <div className="stat-value">2,156</div>
                        <div className="stat-label">Comments & Discussions</div>
                      </div>
                      <div className="stat-card">
                        <FileText size={24} style={{ color: '#4678be' }} />
                        <div className="stat-value">487</div>
                        <div className="stat-label">Documents Shared</div>
                      </div>
                      <div className="stat-card">
                        <Users size={24} style={{ color: '#4678be' }} />
                        <div className="stat-value">63</div>
                        <div className="stat-label">Active Team Members</div>
                      </div>
                      <div className="stat-card">
                        <CheckCircle size={24} style={{ color: '#3BAA72' }} />
                        <div className="stat-value">99%</div>
                        <div className="stat-label">Response Rate</div>
                      </div>
                    </div>
                    <div className="collaboration-examples">
                      <h5>Key Collaboration Scenarios:</h5>
                      <div className="example-scenarios">
                        {[
                          {
                            scenario: 'Daily Construction Meeting Coordination',
                            description: 'Daily stand-up meetings with 15 participants from GC, architects, MEP contractors, and project management. All action items, change orders, and progress updates documented in CAM360 with real-time notifications.',
                            participants: '15 team members daily',
                            documents: '89 meeting notes & action items'
                          },
                          {
                            scenario: 'Change Order Approval Workflow',
                            description: 'CFO and project sponsor @mentioned in change order discussions. All quotes, impact analysis, and approval documents shared. Automated workflow ensures proper authorization before work proceeds.',
                            participants: '12 stakeholders',
                            documents: '34 change orders processed'
                          },
                          {
                            scenario: 'Pre-Leasing & Tenant Coordination',
                            description: 'Real-time collaboration with leasing team, property management, and future tenants. All tenant improvement requests, lease agreements, and move-in schedules tracked and coordinated.',
                            participants: '20 team members + tenants',
                            documents: '156 lease documents & TI plans'
                          },
                          {
                            scenario: 'Safety & Compliance Documentation',
                            description: 'Daily safety reports, inspection results, and compliance documentation shared across all contractors. Automated alerts for any safety incidents or compliance issues.',
                            participants: '25 safety officers & managers',
                            documents: '312 safety reports & inspections'
                          }
                        ].map((example, index) => (
                          <motion.div
                            key={index}
                            className="scenario-card"
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                          >
                            <h6>{example.scenario}</h6>
                            <p>{example.description}</p>
                            <div className="scenario-metrics">
                              <span><Users size={14} /> {example.participants}</span>
                              <span><FileText size={14} /> {example.documents}</span>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Results Summary */}
                <div className="example-results">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                  >
                    <h3>Results & Impact</h3>
                    <div className="results-grid">
                      <div className="result-item">
                        <CheckCircle size={32} style={{ color: '#3BAA72' }} />
                        <h4>4.3% Under Budget</h4>
                        <p>$10.75M saved through AI-driven cost optimization and proactive management</p>
                      </div>
                      <div className="result-item">
                        <Clock size={32} style={{ color: '#4678be' }} />
                        <h4>3 Weeks Ahead of Schedule</h4>
                        <p>AI recommendations and optimized sequencing accelerated project completion</p>
                      </div>
                      <div className="result-item">
                        <TrendingUp size={32} style={{ color: '#3BAA72' }} />
                        <h4>$6.75M Additional Revenue</h4>
                        <p>Earlier completion captured peak market, increased pre-leasing by 15%</p>
                      </div>
                      <div className="result-item">
                        <Shield size={32} style={{ color: '#8B5CF6' }} />
                        <h4>Zero Safety Incidents</h4>
                        <p>Comprehensive safety tracking and collaboration ensured perfect safety record</p>
                      </div>
                      <div className="result-item">
                        <Star size={32} style={{ color: '#F2B740' }} />
                        <h4>99% Team Satisfaction</h4>
                        <p>Improved coordination and visibility across all stakeholders</p>
                      </div>
                      <div className="result-item">
                        <BarChart3 size={32} style={{ color: '#4678be' }} />
                        <h4>100% Compliance</h4>
                        <p>All building codes, permits, and regulations met with proactive AI monitoring</p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </SectionContainer>

          {/* Manufacturing Industry Example Section */}
          <SectionContainer id="manufacturing-example" title="Industry Example: Manufacturing Company">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="pharma-example">
                <div className="example-intro">
                  <h3>Real-World Use Case: TechManufacturing's Smart Factory Expansion</h3>
                  <p className="lead">
                    TechManufacturing, a leading industrial manufacturer, uses CAM360 to manage a $180M smart factory 
                    expansion project involving new production lines, automation systems, and supply chain integration 
                    over 18 months. Here's their complete digital transformation workflow.
                  </p>
                </div>

                {/* Project Setup */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="phase-number">1</div>
                    <div>
                      <h4>Project Creation: "Smart Factory 2.0 - Automated Production Expansion"</h4>
                      <p className="phase-subtitle">Initial Setup & Digital Transformation Planning</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="phase-details">
                      <div className="detail-item">
                        <strong>Project Details:</strong>
                        <ul>
                          <li><strong>Timeline:</strong> 18 months (1.5 years)</li>
                          <li><strong>Budget:</strong> $180 million total investment</li>
                          <li><strong>Team:</strong> 52 internal staff + 18 external contractors</li>
                          <li><strong>Scope:</strong> 3 new automated production lines, IoT integration, quality control systems, warehouse automation</li>
                          <li><strong>Objective:</strong> Increase production capacity by 40%, reduce defects by 60%, implement Industry 4.0</li>
                        </ul>
                      </div>
                      <div className="detail-item">
                        <strong>Initial Setup in CAM360:</strong>
                        <ul>
                          <li>Created project with production targets, quality metrics, and efficiency goals</li>
                          <li>Set up multi-phase financial tracking (Design, Procurement, Installation, Testing, Production)</li>
                          <li>Configured compliance workflows for ISO standards, safety regulations, environmental requirements</li>
                          <li>Integrated with ERP system for inventory, production planning, and supply chain</li>
                          <li>Linked to MES (Manufacturing Execution System) for real-time production monitoring</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Milestones */}
                <div className="example-phase">
                  <motion.div
                    className="phase-header"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                  >
                    <div className="phase-number">2</div>
                    <div>
                      <h4>Milestone Definition: Production Line Phases</h4>
                      <p className="phase-subtitle">Breaking Down the Manufacturing Expansion Timeline</p>
                    </div>
                  </motion.div>
                  <div className="phase-content">
                    <div className="milestones-grid">
                      {[
                        { name: 'Design & Engineering', duration: 'Months 1-3', status: 'completed', color: '#3BAA72' },
                        { name: 'Equipment Procurement', duration: 'Months 4-6', status: 'completed', color: '#3BAA72' },
                        { name: 'Factory Layout & Infrastructure', duration: 'Months 7-9', status: 'inProgress', color: '#4678be' },
                        { name: 'Production Line Installation', duration: 'Months 10-13', status: 'pending', color: '#687484' },
                        { name: 'Automation & IoT Integration', duration: 'Months 14-15', status: 'pending', color: '#687484' },
                        { name: 'Testing & Quality Validation', duration: 'Months 16-17', status: 'pending', color: '#687484' },
                        { name: 'Production Ramp-Up & Go-Live', duration: 'Month 18', status: 'pending', color: '#687484' }
                      ].map((milestone, index) => (
                        <motion.div
                          key={index}
                          className="milestone-card"
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          whileHover={{ scale: 1.02, y: -4 }}
                        >
                          <div className="milestone-status" style={{ backgroundColor: `${milestone.color}20`, borderColor: milestone.color }}>
                            <div className="status-dot" style={{ backgroundColor: milestone.color }} />
                          </div>
                          <h5>{milestone.name}</h5>
                          <p>{milestone.duration}</p>
                          <span className="status-badge" style={{ color: milestone.color }}>
                            {milestone.status === 'completed' ? '✓ Completed' : 
                             milestone.status === 'inProgress' ? '→ In Progress' : 
                             '○ Pending'}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                    <div className="phase-note">
                      <Info size={18} />
                      <p>
                        <strong>AI Insight:</strong> CAM360's AI analyzed equipment delivery schedules and identified that 
                        starting infrastructure work 2 weeks early would prevent a 4-week delay bottleneck, saving $2.8M 
                        in production downtime costs.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Results Summary */}
                <div className="example-results">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                  >
                    <h3>Results & Impact</h3>
                    <div className="results-grid">
                      <div className="result-item">
                        <CheckCircle size={32} style={{ color: '#3BAA72' }} />
                        <h4>5% Under Budget</h4>
                        <p>$9M saved through AI-driven optimization and proactive management</p>
                      </div>
                      <div className="result-item">
                        <Clock size={32} style={{ color: '#4678be' }} />
                        <h4>4 Weeks Ahead of Schedule</h4>
                        <p>AI recommendations and optimized sequencing accelerated project completion</p>
                      </div>
                      <div className="result-item">
                        <TrendingUp size={32} style={{ color: '#3BAA72' }} />
                        <h4>55% Production Increase</h4>
                        <p>Optimized line design exceeded original 40% target, generating $3.6M additional revenue</p>
                      </div>
                      <div className="result-item">
                        <Shield size={32} style={{ color: '#8B5CF6' }} />
                        <h4>75% Defect Reduction</h4>
                        <p>AI-powered quality optimization exceeded 60% target, saving $8M annually</p>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </SectionContainer>

          {/* Contact Support Section */}
          <SectionContainer id="contact" title="Contact Support">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="contact-section">
                <div className="contact-card">
                  <Mail className="contact-icon" size={32} />
                  <h3>Get Help</h3>
                  <p>Need assistance? Our support team is here to help.</p>
                  <div className="contact-methods">
                    <div className="contact-method">
                      <strong>Email:</strong> support@cam360.com
                    </div>
                    <div className="contact-method">
                      <strong>Phone:</strong> 1-800-CAM360-1
                    </div>
                    <div className="contact-method">
                      <strong>Hours:</strong> Monday - Friday, 9 AM - 6 PM EST
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </SectionContainer>
        </main>
      </div>
    </div>
  );
};

// Section Container Component
const SectionContainer = ({ id, title, children }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id={id} ref={ref} className="help-section">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <h2 className="section-title">{title}</h2>
        {children}
      </motion.div>
    </section>
  );
};

export default CAM360HelpCenter;

